'use client';

import * as React from 'react';
import { useMemo } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Fade from 'embla-carousel-fade';
import { useTranslations } from 'next-intl';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { Button } from '@/components/ui/button';
import CmsImage from '@/components/ui/CmsImage';
import { useCarouselNeedsNavigation } from '@/hooks/useCarouselNeedsNavigation';
import { stripHtml, getImageUrl } from '@/lib/homepage-mappers';
import { getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { SustainabilitySectionWithTestimonial } from '@/types/sustainability';

const BG_IMAGE = '/images/stan_img.png';

const SLIDE_AVATARS = [
  '/images/chairman.png',
  '/images/chairman_1.png',
  '/images/chairman.png',
] as const;

const SLIDE_KEYS = ['slide0', 'slide1', 'slide2'] as const;

/** Shadcn-style controls: white tile, subtle border, dark icon (matches design on dark hero). */
const stanCarouselControlClass =
  'h-10 w-10 min-h-[40px] min-w-[40px] shrink-0 rounded-md border border-white/45 bg-white text-neutral-900 shadow-sm hover:bg-white/90 hover:text-neutral-900 sm:h-11 sm:w-11 sm:min-h-[44px] sm:min-w-[44px]';

interface SustainabilityStanQuoteSectionProps {
  section?: SustainabilitySectionWithTestimonial;
}

export default function SustainabilityStanQuoteSection({
  section,
}: SustainabilityStanQuoteSectionProps) {
  const t = useTranslations('sustainability.stanSection');
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const fadePlugin = React.useMemo(() => Fade(), []);

  const slides = useMemo(() => {
    if (section?.testimonial && section.testimonial.length > 0) {
      return section.testimonial.map((entry, i) => ({
        quote: stripHtml(entry.content),
        name: entry.author || t(`slide${i}.name`),
        role: entry.aboutAuthor || t(`slide${i}.role`),
        avatar: getImageUrl(entry.authorImage as never) || SLIDE_AVATARS[i] || SLIDE_AVATARS[0],
      }));
    }
    return SLIDE_KEYS.map((key, i) => ({
      quote: t(`${key}.quote`),
      name: t(`${key}.name`),
      role: t(`${key}.role`),
      avatar: SLIDE_AVATARS[i],
    }));
  }, [section, t]);

  const needsNavigation = useCarouselNeedsNavigation(api, slides.length);

  React.useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    onSelect();
    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  const active = slides[current] ?? slides[0];

  const heading = section?.heading?.trim() || t('heading');
  const subText = stripHtml(section?.subHeading);
  // Full-bleed background: use original upload, not Strapi's ~1000px `large` variant.
  const cmsBgUrl = getStrapiOriginalMediaUrl(section?.bgImage as never);
  const bgSrc = cmsBgUrl || BG_IMAGE;

  return (
    <section
      className="relative z-10 min-h-[640px] w-full overflow-hidden bg-black sm:min-h-[700px] lg:min-h-[min(105vh,900px)]"
      aria-labelledby="sustainability-stan-heading"
    >
      <div className="absolute inset-0">
        <CmsImage
          src={bgSrc}
          alt={t('backgroundAlt')}
          fill
          className="object-cover object-center"
          sizes="100vw"
          quality={100}
          unoptimized
          priority
          fallbackSrc={MEDIA_PLACEHOLDER}
        />
        {/* Tint read legibility top→bottom; no blur on upper sky */}
        <div
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(6,40,28,0.92)_0%,rgba(6,40,28,0.55)_30%,rgba(6,40,28,0.12)_62%,transparent_84%)]"
          aria-hidden
        />
        {/* Backdrop blur only in lower band; masked so it fades out upward (not full-frame blur) */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[min(52vh,580px)] max-h-[68%] backdrop-blur-sm [mask-image:linear-gradient(to_top,black_25%,black_55%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_top,black_25%,black_55%,transparent_100%)]"
          aria-hidden
        />
      </div>

      <div className="relative flex min-h-[640px] flex-col justify-end sm:min-h-[700px] lg:min-h-[min(105vh,900px)]">
        <div className="container mx-auto pb-10 sm:pb-12 md:pb-14 max-sm:px-4">
          <div className="grid items-end gap-8 md:gap-10 lg:grid-cols-2 lg:gap-12 xl:gap-16">
            <div>
              <h2
                id="sustainability-stan-heading"
                className="text-[1.75rem] font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]"
              >
                {heading}
              </h2>
              {subText ? (
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/95 sm:text-base md:text-lg">
                  {subText}
                </p>
              ) : (
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/95 sm:text-base md:text-lg">
                  {t('subLead')}{' '}
                  <strong className="font-semibold text-white">{t('subBold')}</strong>{' '}
                  {t('subTrail')}
                </p>
              )}
            </div>

            <div className="flex min-h-0 flex-col justify-end lg:pl-4">
              <div className="relative min-h-[8rem] w-full sm:min-h-[9rem] md:min-h-[9.5rem]">
                <Carousel
                  setApi={setApi}
                  opts={{ loop: true, containScroll: false }}
                  plugins={[fadePlugin]}
                  className="w-full text-white"
                >
                  <CarouselContent className="-ml-0">
                    {slides.map((slide) => (
                      <CarouselItem key={slide.quote} className="basis-full pl-0">
                        <blockquote className="text-sm leading-relaxed text-white/95 sm:text-base md:text-lg">
                          <p className="italic">&ldquo;{slide.quote}&rdquo;</p>
                        </blockquote>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 sm:mt-8 sm:gap-5">
                <div
                  key={current}
                  className="min-w-0 flex-1 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-300"
                >
                  <p className="text-sm font-semibold text-white sm:text-base">{active.name}</p>
                  <p className="text-xs text-white/80 sm:text-sm">{active.role}</p>
                </div>
                <div className="flex shrink-0 gap-2 sm:ml-auto">
                  {needsNavigation ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={stanCarouselControlClass}
                        onClick={() => api?.scrollPrev()}
                        aria-label={t('prevSlide')}
                      >
                        <ArrowLeft className="h-5 w-5" />
                        <span className="sr-only">{t('prevSlide')}</span>
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={stanCarouselControlClass}
                        onClick={() => api?.scrollNext()}
                        aria-label={t('nextSlide')}
                      >
                        <ArrowRight className="h-5 w-5" />
                        <span className="sr-only">{t('nextSlide')}</span>
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
